// Enhanced Graph configuration constants with external API support

export const WORLD_CENTER = { x: 0, y: 0 };
export const NODE_SIZE = { width: 280, height: 60 };
export const NODE_SPACING = { x: NODE_SIZE.width * 0.5, y: NODE_SIZE.height * 0.5 };

// Each successive group of nodes (i.e., depth => depth + 1) is projected in a different x and y
// directions. This constant defines a convention for 6 possible projection directions, which are
// successively applied to each node group. In turn, this means that each new dialog branch is
// visually distinct from the previous.
export const RAD_PER_DEPTH = Math.PI / 3;

// The resting (not current) node fill and shadow, the current and root
// glows, and the selected state.
// NodeComponent used to hard-code these, which left dark mode no way to
// replace them.
const LIGHT_NODE_STATES = {
  surfaceMuted: 'rgba(255, 255, 255, 0.85)',
  shadow: '0 4px 12px rgba(0, 0, 0, 0.04), 0 2px 4px rgba(0, 0, 0, 0.06)',
  selectedBorder: '#3B82F6',
  selectedBg: 'rgba(59, 130, 246, 0.08)',
  selectedRootBg: 'rgba(59, 130, 246, 0.15)',
  selectedShadow: '0 0 0 4px rgba(59, 130, 246, 0.5), 0 0 30px rgba(59, 130, 246, 0.6), 0 12px 40px rgba(59, 130, 246, 0.3)',
  // The current node's neutral lift, under its colored ring and halo.
  lift: '0 12px 24px rgba(0, 0, 0, 0.08)',
  // Alphas for the root and current glows, applied to the scheme's own color:
  // rest is the root at rest, halo the soft spread, ring the tight outline.
  glow: { rest: 0.2, halo: 0.3, ring: 0.3 },
};

export const colorSchemes = {
  default: {
    primary: 'rgb(100, 116, 139)', // slate-500
    secondary: 'rgb(71, 85, 105)', // slate-600
    accent: 'rgb(148, 163, 184)', // slate-400
    bg: 'rgb(248, 250, 252)', // slate-50
    surface: 'rgb(255, 255, 255)', // white
    text: 'rgb(51, 65, 85)', // slate-700
    textMuted: 'rgb(100, 116, 139)', // slate-500
    border: 'rgb(226, 232, 240)', // slate-200
    success: 'rgb(34, 197, 94)', // green-500
    warning: 'rgb(245, 158, 11)', // amber-500
    error: 'rgb(239, 68, 68)', // red-500
    info: 'rgb(99, 102, 241)', // indigo-500
    // Root node colors
    rootBg: 'rgba(99, 102, 241, 0.08)', // indigo with transparency
    rootBorder: 'rgb(99, 102, 241)', // indigo-500
    rootText: 'rgb(67, 56, 202)', // indigo-700
    ...LIGHT_NODE_STATES,
  },
  blue: {
    primary: 'rgb(59, 130, 246)', // blue-500
    secondary: 'rgb(37, 99, 235)', // blue-600
    accent: 'rgb(96, 165, 250)', // blue-400
    bg: 'rgb(239, 246, 255)', // blue-50
    surface: 'rgb(255, 255, 255)', // white
    text: 'rgb(30, 58, 138)', // blue-800
    textMuted: 'rgb(59, 130, 246)', // blue-500
    border: 'rgb(191, 219, 254)', // blue-200
    success: 'rgb(34, 197, 94)', // green-500
    warning: 'rgb(245, 158, 11)', // amber-500
    error: 'rgb(239, 68, 68)', // red-500
    info: 'rgb(59, 130, 246)', // blue-500
    rootBg: 'rgba(59, 130, 246, 0.08)',
    rootBorder: 'rgb(59, 130, 246)',
    rootText: 'rgb(30, 58, 138)',
    ...LIGHT_NODE_STATES,
  },
  purple: {
    primary: 'rgb(139, 92, 246)', // violet-500
    secondary: 'rgb(124, 58, 237)', // violet-600
    accent: 'rgb(167, 139, 250)', // violet-400
    bg: 'rgb(250, 250, 255)', // violet-50
    surface: 'rgb(255, 255, 255)', // white
    text: 'rgb(91, 33, 182)', // violet-800
    textMuted: 'rgb(139, 92, 246)', // violet-500
    border: 'rgb(221, 214, 254)', // violet-200
    success: 'rgb(34, 197, 94)', // green-500
    warning: 'rgb(245, 158, 11)', // amber-500
    error: 'rgb(239, 68, 68)', // red-500
    info: 'rgb(139, 92, 246)', // violet-500
    rootBg: 'rgba(139, 92, 246, 0.08)',
    rootBorder: 'rgb(139, 92, 246)',
    rootText: 'rgb(91, 33, 182)',
    ...LIGHT_NODE_STATES,
  },
  green: {
    primary: 'rgb(34, 197, 94)', // green-500
    secondary: 'rgb(22, 163, 74)', // green-600
    accent: 'rgb(74, 222, 128)', // green-400
    bg: 'rgb(240, 253, 244)', // green-50
    surface: 'rgb(255, 255, 255)', // white
    text: 'rgb(20, 83, 45)', // green-800
    textMuted: 'rgb(34, 197, 94)', // green-500
    border: 'rgb(187, 247, 208)', // green-200
    success: 'rgb(34, 197, 94)', // green-500
    warning: 'rgb(245, 158, 11)', // amber-500
    error: 'rgb(239, 68, 68)', // red-500
    info: 'rgb(34, 197, 94)', // green-500
    rootBg: 'rgba(34, 197, 94, 0.08)',
    rootBorder: 'rgb(34, 197, 94)',
    rootText: 'rgb(20, 83, 45)',
    ...LIGHT_NODE_STATES,
  },
  orange: {
    primary: 'rgb(249, 115, 22)', // orange-500
    secondary: 'rgb(234, 88, 12)', // orange-600
    accent: 'rgb(251, 146, 60)', // orange-400
    bg: 'rgb(255, 247, 237)', // orange-50
    surface: 'rgb(255, 255, 255)', // white
    text: 'rgb(154, 52, 18)', // orange-800
    textMuted: 'rgb(249, 115, 22)', // orange-500
    border: 'rgb(254, 215, 170)', // orange-200
    success: 'rgb(34, 197, 94)', // green-500
    warning: 'rgb(249, 115, 22)', // orange-500
    error: 'rgb(239, 68, 68)', // red-500
    info: 'rgb(249, 115, 22)', // orange-500
    rootBg: 'rgba(249, 115, 22, 0.08)',
    rootBorder: 'rgb(249, 115, 22)',
    rootText: 'rgb(154, 52, 18)',
    ...LIGHT_NODE_STATES,
  }
};

// Dark counterparts of colorSchemes, keyed the same. Dark mode is neutral
// black rather than navy: nodes are raised greys on a near-black canvas, with
// neutral text and hairline borders. The scheme's hue appears only where it
// means something: the current node's border, the root's border, both glows,
// and selection.
//
// Fills are close to opaque. Edges run to node centers, and on a dark canvas
// a translucent node shows the line passing underneath it.
const DARK_CANVAS = [10, 10, 10];
const tintOverCanvas = (rgb, amount) => {
  const mixed = rgb.split(',').map((c, i) =>
    Math.round(DARK_CANVAS[i] + (Number(c) - DARK_CANVAS[i]) * amount));
  return `rgba(${mixed.join(', ')}, 0.96)`;
};

const darkScheme = ({ primary, root = primary }) => ({
  primary,
  secondary: primary,
  accent: primary,
  bg: 'rgb(10, 10, 10)',
  surface: 'rgb(30, 30, 30)',
  surfaceMuted: 'rgba(23, 23, 23, 0.96)',
  text: 'rgb(229, 229, 229)', // neutral-200
  textMuted: 'rgb(163, 163, 163)', // neutral-400
  border: 'rgba(255, 255, 255, 0.1)',
  success: 'rgb(74, 222, 128)', // green-400
  warning: 'rgb(251, 191, 36)', // amber-400
  error: 'rgb(248, 113, 113)', // red-400
  info: primary,
  rootBg: 'rgba(28, 28, 28, 0.96)',
  rootBorder: root,
  rootText: 'rgb(250, 250, 250)', // neutral-50
  shadow: '0 4px 14px rgba(0, 0, 0, 0.5), 0 1px 2px rgba(0, 0, 0, 0.6)',
  lift: '0 12px 28px rgba(0, 0, 0, 0.6)',
  // Black swallows a soft halo faster than white does, so the halo and the
  // resting glow are a touch stronger here, and the ring is brighter.
  glow: { rest: 0.32, halo: 0.34, ring: 0.4 },
  selectedBorder: '#3B82F6',
  selectedBg: tintOverCanvas('59, 130, 246', 0.08),
  selectedRootBg: tintOverCanvas('59, 130, 246', 0.14),
  selectedShadow: '0 0 0 3px rgba(59, 130, 246, 0.35), 0 0 22px rgba(59, 130, 246, 0.25), 0 12px 32px rgba(0, 0, 0, 0.6)',
});

export const darkColorSchemes = {
  default: darkScheme({ primary: 'rgb(163, 163, 163)', root: 'rgb(129, 140, 248)' }),
  blue: darkScheme({ primary: 'rgb(96, 165, 250)' }), // blue-400
  purple: darkScheme({ primary: 'rgb(167, 139, 250)' }), // violet-400
  green: darkScheme({ primary: 'rgb(74, 222, 128)' }), // green-400
  orange: darkScheme({ primary: 'rgb(251, 146, 60)' }), // orange-400
};

// Edges on the canvas: the curve, its arrowhead, and the dots at each end.
// The ring around each dot is the canvas color, so it reads as a cut-out.
export const EDGE_COLORS = {
  light: {
    stroke: 'rgb(148, 163, 184)', // slate-400
    opacity: '0.6',
    start: 'rgb(148, 163, 184)',
    end: 'rgb(59, 130, 246)', // blue-500
    ring: 'white',
    shadow: 'drop-shadow(0 1px 2px rgba(0, 0, 0, 0.1))',
  },
  dark: {
    stroke: 'rgb(115, 115, 115)', // neutral-500
    opacity: '0.6',
    start: 'rgb(115, 115, 115)',
    end: 'rgb(96, 165, 250)', // blue-400
    ring: 'rgb(10, 10, 10)',
    shadow: 'drop-shadow(0 1px 2px rgba(0, 0, 0, 0.6))',
  },
};

export const ANIMATION_SETTINGS = {
  CAMERA_TRANSITION_DURATION: 300,
  KEYBOARD_THROTTLE_MS: 50,
  GENERATION_STATUS_UPDATE_MS: 1000
};

// Enhanced LLM configuration supporting both local and external models
export const LLM_CONFIG = {
  // Local configuration (Ollama)
  LOCAL: {
    DEFAULT_BASE_URL: 'http://localhost:11434',
    // gemma3:4b (3.3GB) stays the default: gemma4's smallest variant is 7.2GB
    // and the line has no sub-1GB tier. gemma4:e4b is offered in the setup
    // guide for anyone who wants the newer, multimodal generation.
    DEFAULT_MODEL: 'gemma3:4b',
    LIGHTWEIGHT_MODEL: 'gemma3:270m',
    TAGS_ENDPOINT: '/api/tags',
    GENERATE_ENDPOINT: '/api/generate'
  },

  // See https://mlc.ai/models and
  // https://huggingface.co/models?pipeline_tag=text-generation&library=transformers.js
  // Browser models. `size` is the actual download at the listed dtype, summed
  // over the ONNX graph and its external .onnx_data weights - not the
  // parameter count. WebLLMProgressTracker parses it for its progress maths,
  // and the wizard quotes it for consent, so it has to be the real figure.
  WEBLLM: {
    "onnx-community/gemma-3-270m-it-ONNX": {
      name: 'Gemma 3 270M',
      params: '270M',
      size: '273 MB',
      dtype: 'q4f16',
      performance: 'Low',
      recommended: true
    },
    "onnx-community/Qwen3-0.6B-ONNX": {
      name: 'Qwen3 0.6B',
      params: '0.6B',
      size: '570 MB',
      dtype: 'q4f16',
      performance: 'Low-Medium',
      recommended: false
    },
    "onnx-community/gemma-3-1b-it-ONNX": {
      name: 'Gemma 3 1B',
      params: '1B',
      size: '764 MB',
      dtype: 'q4f16',
      performance: 'Medium',
      recommended: false
    },
    'gemma3-1b-it-q4f16_1-MLC': {
      name: 'Gemma 3 1B (MLC)',
      params: '1B',
      size: '711 MB',
      dtype: 'q4f16',
      performance: 'Medium',
      recommended: false
    }
  },

  // External API configurations.
  // Single source of truth for the Google model list: ModelSelector, the setup
  // wizard and the installation guide all derive from this.
  EXTERNAL: {
    GOOGLE: {
      MODELS: {
        'gemini-3.5-flash-lite': {
          name: 'Gemini 3.5 Flash Lite',
          description: 'Fastest and most cost-effective',
          recommended: true
        },
        'gemini-3.6-flash': {
          name: 'Gemini 3.6 Flash',
          description: 'Balanced speed and capability',
          recommended: false
        },
        'gemini-3.7-flash': {
          name: 'Gemini 3.7 Flash',
          description: 'Most capable, for complex reasoning',
          recommended: false
        }
      },
      DEFAULT_CONFIG: {
        temperature: 0.7,
        maxOutputTokens: 8192,
        topK: 40,
        topP: 0.95
      }
    }
  },

  // Backwards compatibility
  BASE_URL: 'http://localhost:11434',
  MODEL: 'gemma3:4b',
  LW_MODEL: 'gemma3:270m',
  TAGS_ENDPOINT: '/api/tags',
  GENERATE_ENDPOINT: '/api/generate'
};

// Model configuration defaults
export const DEFAULT_MODEL_CONFIGS = {
  DEMO: {
    type: 'demo',
    name: 'Demo Mode',
    description: 'Try graphible with sample content',
    model: "demo"
  },
  LOCAL: {
    type: 'local',
    address: LLM_CONFIG.LOCAL.DEFAULT_BASE_URL,
    model: LLM_CONFIG.LOCAL.DEFAULT_MODEL
  },
  EXTERNAL: {
    type: 'external',
    provider: 'google',
    model: 'gemini-3.5-flash-lite',
    apiKey: ''
  },
  WEBLLM: {
    type: 'webllm',
    model: 'onnx-community/gemma-3-270m-it-ONNX',
    dtype: 'q4f16'
  }
};

// Default to demo mode instead of WebLLM
export const DEFAULT_MODEL_CONFIG = DEFAULT_MODEL_CONFIGS.DEMO;

// API endpoints and configuration (kept for reference but using SDK now)
export const API_INFO = {
  google: {
    sdkPackage: '@google/genai',
    documentationUrl: 'https://github.com/google-gemini/generative-ai-js',
    supportedModels: Object.keys(LLM_CONFIG.EXTERNAL.GOOGLE.MODELS)
  }
};

// Error messages for different connection failures
export const ERROR_MESSAGES = {
  LOCAL_CONNECTION_FAILED: 'Cannot connect to local LLM server. Please ensure Ollama is running and accessible.',
  EXTERNAL_API_FAILED: 'Cannot connect to external API. Please check your API key and internet connection.',
  INVALID_API_KEY: 'Invalid API key. Please check your credentials.',
  MODEL_NOT_FOUND: 'The specified model is not available or accessible.',
  RATE_LIMIT_EXCEEDED: 'API rate limit exceeded. Please try again later.',
  NETWORK_ERROR: 'Network error occurred. Please check your connection.',
  // Consent-related errors
  USER_DECLINED_DOWNLOAD: 'Model download was declined by user.',
  CONSENT_REQUIRED: 'User consent is required before downloading AI models.',
  MODEL_DOWNLOAD_BLOCKED: 'Model download blocked - consent not granted.'
};

// How a model's answer becomes nodes.
//
//   graph  - ask for several JSON node objects and lay each one out as its own
//            node, the original behaviour.
//   single - let the model answer normally and keep the whole reply in one
//            node. Branching and navigation then come from the graph rather
//            than from decomposing a single reply.
export const RESPONSE_MODES = {
  GRAPH: 'graph',
  SINGLE: 'single'
};

export const RESPONSE_MODE_LABELS = {
  [RESPONSE_MODES.GRAPH]: {
    name: 'Graph',
    description: 'Break the answer into connected nodes'
  },
  [RESPONSE_MODES.SINGLE]: {
    name: 'Single',
    description: 'Keep the whole answer in one node'
  }
};

export const DEFAULT_RESPONSE_MODE = RESPONSE_MODES.GRAPH;

// Graph mode asks for several strictly-formatted JSON objects. Models of this
// size do not reliably produce that, and the reply then falls back to a single
// node anyway - so single mode is both the honest default and the better
// experience for them.
export const RESPONSE_MODE_BY_BACKEND = {
  webllm: RESPONSE_MODES.SINGLE,
};

export const preferredResponseModeFor = (modelConfig) =>
  RESPONSE_MODE_BY_BACKEND[modelConfig?.type] ?? null;
export const RESPONSE_MODE_STORAGE_KEY = 'graphible-response-mode';

export const WEBLLM_STATE = {
  NULL: '',
  DOWNLOADING: 'downloading',
  RELOADING: 'reloading',
  DONE: 'done',
};

export const CONSENT_TYPES = {
  WEBLLM_DOWNLOAD: 'webllm-download',
  DATA_COLLECTION: 'data-collection',
  ANALYTICS: 'analytics'
};

export const CONSENT_STATUS = {
  NOT_REQUESTED: 'not-requested',
  PENDING: 'pending',
  GRANTED: 'granted',
  DENIED: 'denied',
  EXPIRED: 'expired'
};

export const BROWSER_LLM_PROVIDERS = {
  MLC_AI__WEB_LLM: "@mlc-ai/web-llm",
  TRANSFORMERS_JS: "@huggingface/transformers"
};

export const BROWSER_LLM_TO_PROVIDER = new Map([
  ['gemma3-1b-it-q4f16_1-MLC', BROWSER_LLM_PROVIDERS.MLC_AI__WEB_LLM],
  ['onnx-community/gemma-3-270m-it-ONNX', BROWSER_LLM_PROVIDERS.TRANSFORMERS_JS],
  ['onnx-community/Qwen3-0.6B-ONNX', BROWSER_LLM_PROVIDERS.TRANSFORMERS_JS],
  ['onnx-community/gemma-3-1b-it-ONNX', BROWSER_LLM_PROVIDERS.TRANSFORMERS_JS]
]);

// The browser model the setup wizard actually configures, for UI copy.
// Derived so download sizes and names cannot drift from LLM_CONFIG.WEBLLM.
export const DEFAULT_WEBLLM_MODEL_INFO =
  LLM_CONFIG.WEBLLM[DEFAULT_MODEL_CONFIGS.WEBLLM.model];

// Google models as an ordered array, for components that render a list.
export const GOOGLE_MODEL_LIST = Object.entries(LLM_CONFIG.EXTERNAL.GOOGLE.MODELS)
  .map(([id, info]) => ({ id, ...info }));

// Every id here was read out of a live account's own quota buckets (see
// scripts/probe-code-assist.mjs), not from a changelog. Nothing older than
// Gemini 3 is offered, here or from discovery - the account still has 2.5
// quota and it still works, but it is not what anyone should be handed. Two corrections worth
// keeping in mind, both of which shipped broken first:
//
//   gemini-3.5-flash  404s - it is not in this vocabulary, despite the name
//                     existing on the Developer API.
//   *-preview ids     work. They were removed once on the theory that preview
//                     models need account access we cannot detect; in fact
//                     gemini-3.1-pro-preview answered 429 "exhausted", which
//                     means the id was valid all along and only the quota was
//                     spent. 404 is a bad name; 429 is a good one.
//
// Code Assist speaks a different model vocabulary from the Gemini Developer
// API - gemini-3.5-flash-lite exists on one and not the other, and sending the
// wrong name returns "Requested entity was not found" with nothing to say which
// entity. Ids are from gemini-cli's packages/core/src/config/models.ts
// (VALID_GEMINI_MODELS), which is the only published list of what this endpoint
// accepts.
//
// Being in that list is necessary but not sufficient. gemini-cli never sends a
// picked id straight through: resolveModel() rewrites it against per-account
// state fetched at startup - hasAccessToPreview, a gemini-3.1 launch flag, and
// a GEMINI_3_5_FLASH_GA_LAUNCHED experiment - and substitutes a GA model when
// the account lacks preview access. We send the id verbatim, so only ids that
// need no such access can be offered here. That is what ruled out
// gemini-3.1-pro-preview and gemini-3-pro-preview: valid strings, rejected for
// an account without preview access.
//
// gemini-3.7-flash is Developer-API-only and is not in this vocabulary at all.
export const CODE_ASSIST_MODELS = {
  'gemini-3.1-flash-lite': {
    name: 'Gemini 3.1 Flash Lite',
    description: 'Fastest, and the lightest use of your allowance',
    recommended: true,
  },
  'gemini-3-flash-preview': {
    name: 'Gemini 3 Flash Preview',
    description: 'Balanced speed and capability',
    recommended: false,
  },
  'gemini-3.1-pro-preview': {
    name: 'Gemini 3.1 Pro Preview',
    description: 'Newest and most capable, and the heaviest use of your allowance',
    recommended: false,
  },
};

// Names here are transliterations of the wire id, never marketing names. These
// are internal ids - `-tiered`, `-high` and `-low` are routing tiers, and they
// sit in the same quota response as `chat_20706` and `tab_flash_lite_preview` -
// so presenting `gemini-3.7-flash-tiered` as "Gemini 3.7 Flash" would name a
// product Google does not publish. The panel shows the id alongside the label
// for the same reason.
//
// The Antigravity surface: a different and much larger catalog than Code
// Assist's - Gemini 3.5/3.6/3.7, Claude and gpt-oss, on a separate quota. Every
// id below was read from a live account's quota buckets and confirmed to
// generate.
//
// **Which surface you get is decided by the User-Agent**, not by the OAuth
// client: a request carrying Antigravity's own string resolves the
// `aicode-consumers` project and these models, while any browser string
// resolves the Code Assist project and 404s all of them. A web page cannot set
// that header - it is forbidden to `fetch` - so these are reachable only where
// the User-Agent already says Antigravity, such as inside Antigravity's own
// browser, or through something else that can set headers.
//
// Treat this as a seed, not an inventory: discovery replaces it with whatever
// the account's own quota buckets name.
//
// Not included: gemini-3.1-pro-high/low and gpt-oss-120b-medium answer 400
// "invalid argument" to this request shape, and the Claude ids want their own.
export const ANTIGRAVITY_MODELS = {
  'gemini-3.7-flash-tiered': {
    name: 'Gemini 3.7 Flash (tiered)',
    description: 'Newest Flash, and the lightest use of your allowance',
    recommended: true,
  },
  'gemini-3.6-flash-high': {
    name: 'Gemini 3.6 Flash (high)',
    description: 'Reasoning turned up',
    recommended: false,
  },
  'gemini-3-flash': {
    name: 'Gemini 3 Flash',
    description: 'Balanced speed and capability',
    recommended: false,
  },
  'gemini-3.1-flash-lite': {
    name: 'Gemini 3.1 Flash Lite',
    description: 'Fastest, when the others are rate limited',
    recommended: false,
  },
};

export const ANTIGRAVITY_MODEL_LIST = Object.entries(ANTIGRAVITY_MODELS)
  .map(([id, info]) => ({ id, ...info }));

export const DEFAULT_ANTIGRAVITY_MODEL =
  (ANTIGRAVITY_MODEL_LIST.find((m) => m.recommended) ?? ANTIGRAVITY_MODEL_LIST[0]).id;

// What to offer before the account has told us anything.
//
// Which models exist depends on the *project* the server resolves, not on which
// client signed in: a browser lands on the Code Assist project (8 models) and
// Antigravity's own browser on `aicode-consumers` (25). Guessing wrong offers
// ids that 404. These four are served by both, so they are safe whichever way
// it goes, and discovery replaces them with the real list the moment it
// answers.
export const SHARED_MODELS = {
  'gemini-3.1-flash-lite': {
    name: 'Gemini 3.1 Flash Lite',
    description: 'Fastest, and the lightest use of your allowance',
    recommended: true,
  },
};

export const SHARED_MODEL_LIST = Object.entries(SHARED_MODELS)
  .map(([id, info]) => ({ id, ...info }));

export const CODE_ASSIST_MODEL_LIST = Object.entries(CODE_ASSIST_MODELS)
  .map(([id, info]) => ({ id, ...info }));

// 'gemini-3.1-pro-preview' -> 'Gemini 3.1 Pro Preview'. Only used for ids the
// static catalog does not name, which is the point: a model Google adds
// tomorrow should appear without a release here.
export const titleForModelId = (id) =>
  id.split('-')
    .map((part) => (/^[0-9.]+$/.test(part) ? part : part.charAt(0).toUpperCase() + part.slice(1)))
    .join(' ');

// The menu, given what the account turned out to have. Ids the static catalog
// names keep their wording; anything else - a preview model this account has
// access to, or one Google adds later - is titled from its id rather than
// hidden. An empty list means discovery did not happen or failed, so the
// static catalog stands.
// The menu, given what the account turned out to have.
//
// Until discovery answers there is nothing to go on but a guess about which
// project the server will resolve, and a wrong guess offers ids that 404 - so
// the seed is the set both projects serve. Once the quota has spoken, that is
// the list, whatever it contains.
export const buildCodeAssistModelList = (discoveredIds) => {
  if (!discoveredIds?.length) return SHARED_MODEL_LIST;

  // Both catalogs are only name sources now; an id in neither is titled from
  // itself.
  const known = { ...CODE_ASSIST_MODELS, ...ANTIGRAVITY_MODELS, ...SHARED_MODELS };
  const preferredId = DEFAULT_CODE_ASSIST_MODEL;

  const list = discoveredIds.map((id) => ({
    id,
    name: known[id]?.name ?? titleForModelId(id),
    description: known[id]?.description ?? '',
    recommended: false,
  }));

  // Keep a recommendation, so the panel still points somewhere on first open.
  const preferred = list.find((m) => m.id === preferredId)
    ?? list.find((m) => m.id.includes('flash-lite'))
    ?? list.find((m) => m.id.includes('flash'))
    ?? list[0];
  preferred.recommended = true;
  return list;
};

export const DEFAULT_CODE_ASSIST_MODEL =
  (CODE_ASSIST_MODEL_LIST.find((m) => m.recommended) ?? CODE_ASSIST_MODEL_LIST[0]).id;

// The recommended Google model, and the fallback for anything unrecognised.
export const RECOMMENDED_GOOGLE_MODEL =
  (GOOGLE_MODEL_LIST.find((m) => m.recommended) ?? GOOGLE_MODEL_LIST[0]).id;

// A model id persisted before the catalog moved on can name a model Google has
// since retired. The saved config is loaded verbatim, so the stale id survives
// upgrades and every request 404s - with an empty body, so nothing surfaces.
// Any id no longer in the catalog is rewritten to the recommended one.
// Each backend is migrated against its own catalog. Code Assist ids that leave
// the catalog are not necessarily retired at Google, so a saved one keeps
// working - but it is no longer offered in the menu, which leaves the user on a
// model they cannot see and cannot switch back to.
export const migrateModelConfig = (config) => {
  if (config?.type === 'code-assist') {
    // These catalogs are seeds, not inventories - the account's own quota is
    // the authority on what it may use, and it routinely names models neither
    // catalog lists. So "unknown here" is not evidence of "wrong", and
    // rewriting on it silently replaces a model the user picked and that works.
    //
    // What *is* evidence: an id belonging to the other client. The two
    // vocabularies do not overlap, so a gemini-cli id under Antigravity was
    // carried across rather than chosen, and would fail every request.
    const antigravity = config.authProvider === 'antigravity';
    const own = antigravity ? ANTIGRAVITY_MODELS : CODE_ASSIST_MODELS;
    const foreign = antigravity ? CODE_ASSIST_MODELS : ANTIGRAVITY_MODELS;
    // The catalogs overlap - gemini-3.1-flash-lite is served by both surfaces -
    // so being in the other one is only evidence of crossing over when it is
    // absent from this one.
    if (Object.hasOwn(own, config.model) || !Object.hasOwn(foreign, config.model)) return config;
    return { ...config, model: antigravity ? DEFAULT_ANTIGRAVITY_MODEL : DEFAULT_CODE_ASSIST_MODEL };
  }
  if (config?.type !== 'external' || config?.provider !== 'google') return config;
  if (Object.hasOwn(LLM_CONFIG.EXTERNAL.GOOGLE.MODELS, config.model)) return config;
  return { ...config, model: RECOMMENDED_GOOGLE_MODEL };
};
